import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import TableModal from "../../../../components/reusableComponent/TableModal";
import CustomDate from "../../../../components/common/CustomDate";
import { useGetEmailFirstResponseClickToExpandQuery } from "../../../../Services/dashboard/dashboard";
import { useEmailDashboardQuery } from "../../../../Services/dashboard/EmailDashboardApi";
import {
  HourlyDaySeriesTooltip,
  hourlyChartLegendProps,
  hourlyChartTooltipProps,
  formatIsoForDayLabel,
  type HourlyTooltipConfig,
} from "./HourlyDaySeriesTooltip";
import { HourlyVolumeLegend } from "./HourlyVolumeLegend";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

function normalizeDayDateToIsoKey(raw: any): string {
  if (raw == null || raw === "") return "_unknown";
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const n = Math.trunc(raw as number);
    if (n >= 19000101 && n <= 29991231) {
      const s = String(n);
      return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    }
    const ms = n < 1e12 ? n * 1000 : n;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return `_${n}`;
    return formatDateToYyyyMmDd(d);
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    if (/^\d{4}-\d{2}-\d{2}[T ]/.test(t)) return t.slice(0, 10);
    const d = new Date(t);
    if (!Number.isNaN(d.getTime())) return formatDateToYyyyMmDd(d);
    return t || "_unknown";
  }
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return formatDateToYyyyMmDd(d);
  return String(raw);
}

const SENT_HOUR_KEYS = Array.from({ length: 24 }, (_, i) => String(i));

const SENT_COLOR_PALETTE = [
  { color: "#6366f1", name: "Indigo", gradient: "rgba(99, 102, 241, 0.2)" },
  { color: "#8b5cf6", name: "Purple", gradient: "rgba(139, 92, 246, 0.2)" },
  { color: "#10b981", name: "Green", gradient: "rgba(16, 185, 129, 0.2)" },
  { color: "#f59e0b", name: "Amber", gradient: "rgba(245, 158, 11, 0.2)" },
  { color: "#ef4444", name: "Red", gradient: "rgba(239, 68, 68, 0.2)" },
  { color: "#06b6d4", name: "Cyan", gradient: "rgba(6, 182, 212, 0.2)" },
  { color: "#ec4899", name: "Pink", gradient: "rgba(236, 72, 153, 0.2)" },
  { color: "#84cc16", name: "Lime", gradient: "rgba(132, 204, 22, 0.2)" },
];

const EMPTY_SENT_RESULTS: any[] = [];

function buildSentHourlySeries(emailResultsIn: any[]) {
  if (!emailResultsIn?.length) {
    return { chartData: [] as any[], dateLabels: [] as string[] };
  }
  const sortedDays = [...emailResultsIn].sort((a, b) =>
    normalizeDayDateToIsoKey(a.date).localeCompare(normalizeDayDateToIsoKey(b.date))
  );
  const chartData = SENT_HOUR_KEYS.map((hour) => {
    const dataPoint: any = { hour: `${hour}:00` };
    sortedDays.forEach((result) => {
      const isoKey = normalizeDayDateToIsoKey(result.date);
      dataPoint[isoKey] = result.hourly_count[hour] || 0;
    });
    return dataPoint;
  });
  const dateLabels = sortedDays.map((r) => normalizeDayDateToIsoKey(r.date));
  return { chartData, dateLabels };
}

const EMAIL_TABLE_COLUMNS = [
  { label: "Subject", render: (row: any) => row?.subject ?? "—" },
  { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
  { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
  { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
  {
    label: "Created",
    render: (row: any) => {
      const raw = row?.created;
      if (!raw) return "—";
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return "—";
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
    },
  },
];

const Sent = ({ intervalData, startDate, endDate, teamId }) => {
  const navigate = useNavigate();
  const hoveredPointRef = useRef<any>(null);
  const mainChartRef = useRef<HTMLDivElement>(null);
  const modalChartRef = useRef<HTMLDivElement>(null);
  const hourlyTooltipConfigRef = useRef<HourlyTooltipConfig>({
    selDates: [],
    exS: "",
    exE: "",
  });
  const hourlyTooltipContent = useMemo(
    () => (
      <HourlyDaySeriesTooltip
        configRef={hourlyTooltipConfigRef}
        teamId={teamId}
        hoveredPointRef={hoveredPointRef}
      />
    ),
    [teamId]
  );

  const hourlyLegendBindRef = useRef<{
    selDates: string[];
    onToggle: (d: string) => void;
    fullscreen: boolean;
    onSelectAll?: () => void;
    onClearSelection?: () => void;
  }>({
    selDates: [],
    onToggle: () => {},
    fullscreen: false,
  });

  const hourlyLegendRender = useCallback((props: any) => {
    const b = hourlyLegendBindRef.current;
    return (
      <HourlyVolumeLegend
        payload={props?.payload}
        selDates={b.selDates}
        onToggle={b.onToggle}
        fullscreen={b.fullscreen}
        onSelectAll={b.onSelectAll}
        onClearSelection={b.onClearSelection}
      />
    );
  }, []);

  const [fullscreenChart, setFullscreenChart] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [chartType, setChartType] = useState("bar");
  const [hoveredStat, setHoveredStat] = useState(null);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [modalSelectedDates, setModalSelectedDates] = useState<string[]>([]);

  const emailResults =
    intervalData?.emails_per_day_per_hour?.sent ?? EMPTY_SENT_RESULTS;
  const emailResultsSortedByDay = useMemo(
    () =>
      [...emailResults].sort((a, b) =>
        normalizeDayDateToIsoKey(a.date).localeCompare(normalizeDayDateToIsoKey(b.date))
      ),
    [emailResults]
  );

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange]
  );

  const { data: modalOverviewData } = useEmailDashboardQuery(
    {
      dashboardType: "overview",
      selectedTeam: teamId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    }
  );

  const modalEffectiveResults = useMemo(() => {
    if (!fullscreenChart) return emailResults;
    const mr = modalOverviewData?.emails_per_day_per_hour?.sent;
    if (Array.isArray(mr) && mr.length > 0) return mr;
    return emailResults;
  }, [fullscreenChart, modalOverviewData, emailResults]);

  const hourValueFromData = (data) => {
    if (!data?.hour) return null;
    const num = parseInt(String(data.hour).split(":")[0], 10);
    if (Number.isNaN(num) || num < 0 || num > 23) return null;
    return String(num).padStart(2, "0");
  };

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate;
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate;

  const clickToExpandParams =
    tableModalOpen && selectedHour != null && teamId && expandStart && expandEnd
      ? {
          graph_name: "emails_per_day_per_hour",
          value: selectedHour,
          start_date: expandStart,
          end_date: expandEnd,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
          email_type: "sent",
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetEmailFirstResponseClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams }
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw = (clickToExpandData as any)?.results ?? (clickToExpandData as any)?.data ?? clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedHour]);

  const handlePointClick = (data) => {
    const hourVal = hourValueFromData(data);
    if (!teamId || !expandStart || !expandEnd || hourVal == null) return;
    setSelectedHour(hourVal);
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const handleChartClick = () => {
    const data = hoveredPointRef.current;
    if (data) {
      const hourVal = hourValueFromData(data);
      if (hourVal != null && teamId && expandStart && expandEnd) handlePointClick(data);
    }
  };

  const modalSeries = useMemo(
    () => buildSentHourlySeries(modalEffectiveResults),
    [modalEffectiveResults]
  );

  const modalTableData = useMemo(
    () =>
      modalEffectiveResults.map((day) => ({
        date: formatIsoForDayLabel(normalizeDayDateToIsoKey(day.date)),
        ...day.hourly_count,
      })),
    [modalEffectiveResults]
  );

  useEffect(() => {
    if (fullscreenChart) {
      setModalSelectedDates(modalSeries.dateLabels);
    }
  }, [fullscreenChart, modalSeries.dateLabels]);

  const toggleModalDateVisibility = useCallback((date: string) => {
    setModalSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  }, []);

  const chartData = useMemo(
    () =>
      SENT_HOUR_KEYS.map((hour) => {
        const dataPoint: Record<string, any> = { hour: `${hour}:00` };
        emailResultsSortedByDay.forEach((result) => {
          const isoKey = normalizeDayDateToIsoKey(result.date);
          dataPoint[isoKey] = result.hourly_count[hour] || 0;
        });
        return dataPoint;
      }),
    [emailResultsSortedByDay]
  );

  const dateLabels = useMemo(
    () =>
      emailResultsSortedByDay.map((result) =>
        normalizeDayDateToIsoKey(result.date)
      ),
    [emailResultsSortedByDay]
  );

  const sentDatesFingerprint = useMemo(
    () =>
      emailResultsSortedByDay
        .map((d) => normalizeDayDateToIsoKey(d.date))
        .join("|"),
    [emailResultsSortedByDay]
  );

  const totalHourlyCount = emailResultsSortedByDay.reduce((total, day) => {
    const dayTotal = Object.values(day.hourly_count).reduce((sum: number, count: unknown) => sum + (count as number), 0);
    return total + dayTotal;
  }, 0);

  useEffect(() => {
    setSelectedDates(
      emailResultsSortedByDay.map((day) => normalizeDayDateToIsoKey(day.date)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when day keys change (fingerprint), not new [] refs
  }, [sentDatesFingerprint]);

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const AreaChartContainer = AreaChart as any;
  const AreaContainer = Area as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const LineChartContainer = LineChart as any;
  const LineContainer = Line as any;
  const LegendContainer = Legend as any;

  const stats = useMemo(
    () =>
      dateLabels.map((date, idx) => {
        const values = chartData.map((d) => d[date] || 0);
        const total = values.reduce((a, b) => a + b, 0);
        const avg = total / values.length;
        const max = Math.max(...values);
        const peakHour = SENT_HOUR_KEYS[values.indexOf(max)];

        return {
          date,
          total,
          avg: avg.toFixed(1),
          max,
          peakHour: `${peakHour}:00`,
          color: SENT_COLOR_PALETTE[idx % SENT_COLOR_PALETTE.length].color,
        };
      }),
    [chartData, dateLabels]
  );

  const toggleDateVisibility = useCallback((date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  }, []);

  const theme = {
    colors: {
      background: "#f8f9fa",
      surface: "#ffffff",
      primary: "#6366F1",
      primaryHover: "#4F46E5",
      danger: "#EF4444",
      dangerHover: "#DC2626",
      text: "#1f2937",
      textSecondary: "#4b5563",
      textMuted: "#6b7280",
      border: "#e5e7eb",
      borderMedium: "#d1d5db",
      grid: "#f3f4f6",
    },
    shadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  };

  const styles: any = {
    statCard: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      padding: "16px 20px",
      border: "2px solid #e2e8f0",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      minWidth: "180px",
    },
    chartTypeButton: {
      padding: "8px 16px",
      border: "2px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 600,
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    expandButton: {
      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.3s",
      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.75)",
      zIndex: 9999,
      padding: "24px",
      overflow: "auto",
    },
    modalContent: {
      backgroundColor: theme.colors.surface,
      borderRadius: "20px",
      width: "95%",
      maxWidth: "1600px",
      margin: "0 auto",
      padding: "32px",
      position: "relative",
      boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "28px",
      paddingBottom: "20px",
      borderBottom: "2px solid #e2e8f0",
    },
  };

  const renderChart = (
    chartRows: any[],
    height = "100%",
    overlay?: {
      dateLabels: string[];
      selectedDates: string[];
      toggleDate: (d: string) => void;
      gradPrefix: string;
      expandS: string;
      expandE: string;
      fullscreen?: boolean;
      onSelectAllDates?: () => void;
      onClearLegendSelection?: () => void;
    }
  ) => {
    const dl = overlay?.dateLabels ?? dateLabels;
    const selDates = overlay?.selectedDates ?? selectedDates;
    const onToggle = overlay?.toggleDate ?? toggleDateVisibility;
    const gr = overlay?.gradPrefix ?? "emb";
    const exS = overlay?.expandS ?? expandStart;
    const exE = overlay?.expandE ?? expandEnd;
    const isFs = Boolean(overlay?.fullscreen);
    const denseSeries = isFs && dl.length > 8;
    const areaStrokeW = denseSeries ? 1.5 : 3;
    const lineStrokeW = denseSeries ? 2 : 3;
    const lineActiveR = denseSeries ? 5 : 7;
    const getDateColor = (date: string) =>
      SENT_COLOR_PALETTE[
        dl.indexOf(date) >= 0 ? dl.indexOf(date) % SENT_COLOR_PALETTE.length : 0
      ].color;
    hourlyTooltipConfigRef.current = {
      selDates,
      exS,
      exE,
      chartRootRef: isFs ? modalChartRef : mainChartRef,
      dateColors: Object.fromEntries(
        dl.map((date) => [date, getDateColor(date)]),
      ),
    };
    hourlyLegendBindRef.current = {
      selDates,
      onToggle,
      fullscreen: isFs,
      onSelectAll: overlay?.onSelectAllDates,
      onClearSelection: overlay?.onClearLegendSelection,
    };

    const wrapFsChart = (node: React.ReactNode) => {
      if (!isFs) return node;
      const minW = Math.max(920, 680 + dl.length * 28);
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ minWidth: minW, height: "100%" }}>{node}</div>
        </div>
      );
    };

    const commonProps = {
      data: chartRows,
      margin: {
        top: 8,
        right: isFs ? 16 : 30,
        left: isFs ? 12 : 10,
        bottom: isFs ? 16 : 5,
      },
    };

    const commonAxisProps = {
      xAxis: {
        dataKey: "hour",
        tick: {
          fontSize: isFs && dl.length > 10 ? 10 : 12,
          fill: "#64748b",
          fontWeight: 500,
        },
        tickLine: false,
        axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
        interval: isFs && dl.length > 10 ? 1 : 0,
        label: {
          value: "Hour of day",
          position: "insideBottom",
          offset: isFs ? -2 : -5,
          style: {
            fontSize: isFs ? 12 : 13,
            fill: "#475569",
            fontWeight: 600,
          },
        },
      },
      yAxis: {
        tick: {
          fontSize: isFs ? 11 : 12,
          fill: "#64748b",
          fontWeight: 500,
        },
        tickLine: false,
        axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
        tickFormatter: (value) => Math.floor(value).toString(),
        label: {
          value: "Email Count",
          angle: -90,
          position: "insideLeft",
          style: {
            fontSize: isFs ? 12 : 13,
            fill: "#475569",
            fontWeight: 600,
          },
        },
        allowDecimals: false,
        tickCount: isFs ? 6 : 4,

        domain: [
          0,
          (dataMax) => {
            if (!dataMax || dataMax === 0) return 3;
            return Math.ceil(dataMax / 2) * 2;
          },
        ],
      },
    };

    const gridProps = {
      strokeDasharray: "3 3",
      stroke: "#e2e8f0",
      vertical: false,
    };

    if (chartType === "area") {
      return wrapFsChart(
        <ResponsiveChartContainer width="100%" height={height}>
          <AreaChartContainer
            {...commonProps}
            onClick={(data: any) => data?.hour != null && handlePointClick(data)}
          >
            <defs>
              {dl.map((date, index) => (
                <linearGradient
                  key={date}
                  id={`gradient-${gr}-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={SENT_COLOR_PALETTE[index % SENT_COLOR_PALETTE.length].color}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={SENT_COLOR_PALETTE[index % SENT_COLOR_PALETTE.length].color}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dl.map((date, index) => (
              <AreaContainer
                key={date}
                type="monotone"
                name={formatIsoForDayLabel(date)}
                dataKey={date}
                stroke={SENT_COLOR_PALETTE[index % SENT_COLOR_PALETTE.length].color}
                strokeWidth={areaStrokeW}
                fill={`url(#gradient-${gr}-${index})`}
                fillOpacity={selDates.includes(date) ? 1 : 0}
                hide={!selDates.includes(date)}
                isAnimationActive={false}
                animationDuration={1000}
              />
            ))}
          </AreaChartContainer>
        </ResponsiveChartContainer>
      );
    } else if (chartType === "bar") {
      const CellContainer = Cell as any;
      const barMax = isFs && dl.length > 6 ? Math.max(10, 28 - dl.length) : undefined;
      return wrapFsChart(
        <ResponsiveChartContainer width="100%" height={height}>
          <BarChartContainer
            {...commonProps}
            onClick={(data: any) => data?.hour != null && handlePointClick(data)}
          >
            <CartesianGrid {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dl.map((date, index) => (
              <BarContainer
                key={date}
                name={formatIsoForDayLabel(date)}
                dataKey={date}
                fill={SENT_COLOR_PALETTE[index % SENT_COLOR_PALETTE.length].color}
                radius={[8, 8, 0, 0]}
                maxBarSize={barMax}
                hide={!selDates.includes(date)}
                isAnimationActive={false}
                animationDuration={1000}
                cursor={teamId && exS && exE ? "pointer" : "default"}
              >
                <CellContainer cursor={teamId && exS && exE ? "pointer" : "default"} />
              </BarContainer>
            ))}
          </BarChartContainer>
        </ResponsiveChartContainer>
      );
    } else {
      return wrapFsChart(
        <ResponsiveChartContainer width="100%" height={height}>
          <LineChartContainer
            {...commonProps}
            onClick={(data: any) => data?.hour != null && handlePointClick(data)}
          >
            <CartesianGrid {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dl.map((date, index) => (
              <LineContainer
                key={date}
                type="monotone"
                name={formatIsoForDayLabel(date)}
                dataKey={date}
                stroke={SENT_COLOR_PALETTE[index % SENT_COLOR_PALETTE.length].color}
                strokeWidth={selDates.includes(date) ? lineStrokeW : 0}
                dot={false}
                activeDot={{
                  r: lineActiveR,
                  strokeWidth: denseSeries ? 2 : 3,
                  stroke: "#fff",
                  fill: SENT_COLOR_PALETTE[index % SENT_COLOR_PALETTE.length].color,
                }}
                hide={!selDates.includes(date)}
                isAnimationActive={false}
                animationDuration={1000}
              />
            ))}
          </LineChartContainer>
        </ResponsiveChartContainer>
      );
    }
  };

  const FullscreenModal = ({ isOpen, onClose, title }) => {
    if (!isOpen) return null;

    const tableKeys =
      modalTableData && modalTableData.length > 0
        ? Object.keys(modalTableData[0])
        : [];

    const fsSeriesCount = modalSeries.dateLabels.length;
    const fsChartHeightPx = Math.min(
      780,
      Math.max(480, 420 + Math.min(fsSeriesCount, 28) * 12)
    );

    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "2em",
                  color: theme.colors.text,
                  fontWeight: 700,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: theme.colors.textMuted,
                  fontSize: "14px",
                }}
              >
                Interactive email traffic analysis
              </p>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                flexShrink: 0,
              }}
            >
              <div style={{ position: "relative", zIndex: 10002 }}>
                <CustomDate
                  dateRange={modalDateRange}
                  setDateRange={setModalDateRange}
                  unrestricted
                />
              </div>
              <button
                onClick={onClose}
                style={{
                  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "44px",
                  height: "44px",
                  fontSize: "24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "rotate(90deg) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "rotate(0deg) scale(1)";
                }}
              >
                ×
              </button>
            </div>
          </div>

          <div style={{ marginBottom: "32px" }}>
            {fsSeriesCount > 7 && (
              <p
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "13px",
                  color: theme.colors.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                Many days selected: scroll the chart horizontally for clearer
                hours, and use the date list below to show or hide series.
              </p>
            )}
            <div
              ref={modalChartRef}
              style={{
                width: "100%",
                height: `${fsChartHeightPx}px`,
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                overflow: "visible",
                position: "relative",
                cursor:
                  teamId && modalRangeStrings.start && modalRangeStrings.end
                    ? "pointer"
                    : "default",
              }}
              onClick={handleChartClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleChartClick();
              }}
            >
              {renderChart(modalSeries.chartData, "100%", {
                dateLabels: modalSeries.dateLabels,
                selectedDates: modalSelectedDates,
                toggleDate: toggleModalDateVisibility,
                gradPrefix: "fs",
                expandS: modalRangeStrings.start,
                expandE: modalRangeStrings.end,
                fullscreen: true,
                onSelectAllDates: () =>
                  setModalSelectedDates([...modalSeries.dateLabels]),
                onClearLegendSelection: () => setModalSelectedDates([]),
              })}
            </div>
          </div>

          {modalTableData && modalTableData.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: theme.colors.text,
                    fontSize: "1.5em",
                    fontWeight: 700,
                  }}
                >
                  📊 Underlying data
                </h3>
                <span
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {modalTableData.length} rows
                </span>
              </div>

              <div
                style={{
                  overflowX: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  maxHeight: "450px",
                  overflowY: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "14px",
                  }}
                >
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      {tableKeys.map((key) => (
                        <th
                          key={key}
                          style={{
                            padding: "14px 18px",
                            textAlign: "left",
                            borderBottom: "2px solid #cbd5e1",
                            fontWeight: 700,
                            color: theme.colors.textSecondary,
                            whiteSpace: "nowrap",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          {key.replace(/_/g, " ").toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalTableData.map((row, index) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#f8fafc",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e0f2fe")
                        }
                        onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          index % 2 === 0 ? "#ffffff" : "#f8fafc")
                        }
                      >
                        {tableKeys.map((key) => (
                          <td
                            key={key}
                            style={{
                              padding: "14px 18px",
                              borderBottom: "1px solid #e2e8f0",
                              color: theme.colors.textSecondary,
                            }}
                          >
                            {row[key] !== null && row[key] !== undefined
                              ? (row[key] === 0 ? "-" : String(row[key]))
                              : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "visible",
      }}
    >
      <div style={{ padding: "32px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "1.75em",
                fontWeight: 700,
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {`Sent email traffic: ${totalHourlyCount}`}
            </h2>
            <p
              style={{
                margin: "8px 0 0 0",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              Hourly email distribution across selected dates
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                backgroundColor: "#f8fafc",
                padding: "4px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              {["bar", "area", "line"].map((type) => (
                <button
                  key={type}
                  onClick={() => setChartType(type)}
                  style={{
                    ...styles.chartTypeButton,
                    backgroundColor:
                      chartType === type ? "#ffffff" : "transparent",
                    border:
                      chartType === type
                        ? "2px solid #6366f1"
                        : "2px solid transparent",
                    color: chartType === type ? "#6366f1" : "#64748b",
                    boxShadow:
                      chartType === type
                        ? "0 2px 8px rgba(99, 102, 241, 0.2)"
                        : "none",
                  }}
                >
                  {type === "line" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  )}
                  {type === "area" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 3v18h18" />
                      <path d="M18 17V9l-5 5-5-5v8" />
                    </svg>
                  )}
                  {type === "bar" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="12" y1="20" x2="12" y2="10" />
                      <line x1="18" y1="20" x2="18" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="16" />
                    </svg>
                  )}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setFullscreenChart({
                  title: "Sent email traffic by hour",
                })
              }
              style={styles.expandButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(99, 102, 241, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(99, 102, 241, 0.3)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
              Expand View
            </button>
          </div>
        </div>

        <div
          ref={mainChartRef}
          style={{
            width: "100%",
            height: "600px",
            backgroundColor: "#fafbfc",
            padding: "24px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            cursor: teamId && startDate && endDate ? "pointer" : "default",
            position: "relative",
            zIndex: 1,
            overflow: "visible",
          }}
          onClick={handleChartClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleChartClick(); }}
        >
          {renderChart(chartData, "100%")}
        </div>
      </div>

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={() => {
          setTableModalOpen(false);
          setSelectedHour(null);
          setClickToExpandPage(1);
        }}
        overlayZIndex={fullscreenChart ? 10050 : undefined}
        title={selectedHour != null ? `Email volume – Hour ${selectedHour}:00` : "Leads"}
        contentLabel="Email volume by hour click-to-expand"
        emptyMessage="No emails in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx) => row?.thread_id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const threadId = row?.thread_id;
          if (threadId != null) {
            setTableModalOpen(false);
            setSelectedHour(null);
            navigate(`/email/inbox/${threadId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={EMAIL_TABLE_COLUMNS}
      />

      <FullscreenModal
        key={fullscreenChart?.title}
        isOpen={!!fullscreenChart}
        onClose={() => setFullscreenChart(null)}
        title={fullscreenChart?.title || ""}
      />
    </div>
  );
};

export default Sent;
